import React, { useMemo } from "react";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import { CustomInput } from "src/component";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createOrder, getAllInventories } from "src/services/api";
import CustomSelect from "src/component/CustomSelect";
import { Button } from "src/components/ui/button";
import { renderAlert } from "src/utils/functions";

const CreateOrder = ({ refetch, setModalOpen }) => {
  const { mutate, isLoading } = useMutation(createOrder, {
    onSuccess: (res) => {
      renderAlert({
        title: "Success",
        msg: res?.msg,
        type: "success",
      }).then((result) => {
        if (result) {
          setModalOpen(false);
        } else {
          setModalOpen(false);
        }
      });
      refetch();
    },
  });
  const { data } = useQuery({
    queryKey: ["Inventory"],
    queryFn: () => getAllInventories({ has_quantity: true }),
    suspense: true,
  });

  const initialValues = {
    fullName: "",
    email: "",
    inventory: [{ id: "", quantity: "" }],
  };

  const inventoryLookup = useMemo(() => {
    const obj = {};
    data?.data?.forEach((inv) => {
      obj[inv.id] = inv.quantity;
    });
    return obj;
  }, [data]);

  const validationSchema = Yup.object({
    fullName: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    inventory: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().required("Select an item"),
        quantity: Yup.number()
          .typeError("Must be a number")
          .positive("Must be greater than 0")
          .required("Required")
          .test(
            "max-available",
            "Quantity exceeds available stock",
            function (value) {
              const { id } = this.parent;
              if (!id || value == null) return true;
              const maxQty = inventoryLookup[id];
              return value <= maxQty;
            }
          ),
      })
    ),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        mutate({
          fullName: values.fullName,
          email: values.email,
          inventory: { ...values.inventory },
        });
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
        <form className="space-y-4 p-4 mx-auto" onSubmit={handleSubmit}>
          <CustomInput
            name="fullName"
            value={values.fullName}
            title="Full Name"
            placeholder="Enter Full Name"
            onChange={handleChange}
            onBlur={handleBlur}
            formType="fill"
            isRequired
          />
          <CustomInput
            name="email"
            type="email"
            value={values.email}
            title="Email"
            placeholder="Enter Email"
            onChange={handleChange}
            onBlur={handleBlur}
            formType="fill"
            isRequired
          />
          <FieldArray name="inventory">
            {({ push, remove }) => (
              <div>
                <h3 className="font-semibold">Items</h3>
                {values.inventory.map((item, index) => (
                  <div
                    key={index}
                    className="flex space-x-2 items-center w-full"
                  >
                    <CustomSelect
                      containerClassName={"w-1/2 my-[15px]"}
                      className={"h-[53px]"}
                      labelClassName="!mb-5"
                      label={"Inventory Item"}
                      name={`inventory[${index}].id`}
                      options={data?.data?.map(({ id, name, brand }) => ({
                        label: name + "(" + brand + ")",
                        value: id,
                      }))}
                      placeholder="Select item"
                      value={item.id}
                      onChange={(selected) =>
                        setFieldValue(`inventory[${index}].id`, selected)
                      }
                    />
                    <CustomInput
                      title="Quantity"
                      name={`inventory[${index}].quantity`}
                      type="number"
                      placeholder="Enter Quantity"
                      onChange={handleChange}
                      value={item.quantity}
                      onBlur={handleBlur}
                      formType="fill"
                      isRequired
                    />
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-500"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => push({ id: "", quantity: "" })}
                  className="bg-blue-500 text-white px-3 py-1.5 rounded"
                >
                  Add Item
                </button>
              </div>
            )}
          </FieldArray>
          <div className="flex justify-end w-full">
            <Button
              type="submit"
              className="p-4 text-2xl rounded"
              disabled={isLoading}
            >
              {isLoading ? "Adding" : "Add"}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default CreateOrder;
